import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * No `size` axis by design (audit §F2): a badge is a fixed body-2xs pill —
 * scaling it would blur the line to Button, which owns the sized-control role.
 */
const badgeVariants = cva(
	'text-body-2xs inline-flex items-center rounded-full border px-2.5 py-0.5 font-medium whitespace-nowrap',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground border-transparent',
				secondary: 'bg-secondary text-secondary-foreground border-transparent',
				destructive:
					'bg-destructive text-destructive-foreground border-transparent',
				outline: 'border-border text-foreground',
				brand: 'bg-brand-soft text-brand border-transparent',
				warning: 'bg-warning-soft text-warning-fg border-transparent',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

export type BadgeVariant = VariantProps<typeof badgeVariants>

const Badge = ({
	className,
	variant,
	dot = false,
	children,
	...props
}: React.ComponentProps<'span'> &
	BadgeVariant & {
		/** Render a leading status dot that tracks the variant's text color. */
		dot?: boolean
	}) => (
	<span
		data-slot="badge"
		className={cn(badgeVariants({ variant, className }), dot && 'gap-1.5')}
		{...props}
	>
		{dot ? (
			<span
				data-slot="badge-dot"
				aria-hidden="true"
				className="size-1.5 shrink-0 rounded-full bg-current"
			/>
		) : null}
		{children}
	</span>
)

export { Badge, badgeVariants }
